// JD (Joint Director) Applications Management Component
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Alert,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    TextField,
    CircularProgress,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Tooltip,
    Divider,
    Chip,
    Pagination,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Badge,
    Card,
    CardContent,
    Autocomplete,
    Checkbox,
    Modal
} from '@mui/material';
import {
    Visibility as VisibilityIcon,
    GetApp as GetAppIcon,
    History as HistoryIcon,
    Refresh as RefreshIcon,
    AccessTime as AccessTimeIcon,
    CheckBox as CheckBoxIcon,
    CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
    Assignment as AssignmentIcon,
    Send as SendIcon,
    Check as CheckIcon,
    Close as CloseIcon,
    Person as PersonIcon
} from '@mui/icons-material';
import JDApiService from '../../../services/JDApiService';
import { encryptAppId, encryptText } from '../../../utils/encryption';
import axios from 'axios';
import DoApiService from '../../../services/DoApiService';

// Helper functions
   const formatDate = (dateString) => {
        return DoApiService.formatDate(dateString, 'datetime');
    };

const JDSubmittedApplication = () => {
    const navigate = useNavigate();

    // State management
    const [applications, setApplications] = useState([]);
    const [applicationsCount, setApplicationsCount] = useState();
    const [filteredApplications, setFilteredApplications] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selectedAppType, setSelectedAppType] = useState(() => {
        // Restore the previously selected tab from localStorage, fallback to 'toBeProcessed'
        return localStorage.getItem('jdSubmittedApplicationTab') || 'toBeProcessed';
    });
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    // Filter states
    const [searchKeyword, setSearchKeyword] = useState('');
    const [selectedDealingOfficer, setSelectedDealingOfficer] = useState('all');
    const [selectedStage, setSelectedStage] = useState('all');

    // Dropdown options
    const [dealingOfficers, setDealingOfficers] = useState([]);
    const [stages, setStages] = useState([]);

    // Multi-select and allotment states
    const [selectAll, setSelectAll] = useState(false);
    const [selectedApplications, setSelectedApplications] = useState([]);
    const [allotmentModalOpen, setAllotmentModalOpen] = useState(false);
    const [selectedJointDirector, setSelectedJointDirector] = useState('');
    const [jointDirectors, setJointDirectors] = useState([]);
    const [password, setPassword] = useState('');

    // Discipline and Group dialog states
    const [disciplineDialogOpen, setDisciplineDialogOpen] = useState(false);
    const [groupDialogOpen, setGroupDialogOpen] = useState(false);
    const [disciplineData, setDisciplineData] = useState([]);
    const [groupData, setGroupData] = useState([]);

    const [success, setSuccess] = useState(null);

    // Assessors dialog states
    const [assessorsDialogOpen, setAssessorsDialogOpen] = useState(false);
    const [assessorsData, setAssessorsData] = useState([]);
    const [loadingAssessors, setLoadingAssessors] = useState(false);
    const [selectedAssessor, setSelectedAssessor] = useState(null);
    const [assessorAcceptDialogOpen, setAssessorAcceptDialogOpen] = useState(false);
    const [assessorRejectDialogOpen, setAssessorRejectDialogOpen] = useState(false);
    const [assessorRejectRemarks, setAssessorRejectRemarks] = useState('');
    const pageSize = 50;
    // Get user data from localStorage
    const userData = JSON.parse(localStorage.getItem('userData') || '{}');
    const orgId = userData.orgId;
    const appId = userData.appId;
    const userTypeId = userData.userTypeId;

    // Load applications based on button type - without dependencies that cause re-renders
    const loadApplicationsStable = useCallback(async (appType = selectedAppType, pageNum = page, searchParams = {}) => {
        if (!orgId) {
            setError('User session invalid. Please login again.');
            return;
        }

        setLoading(true);
        setError(null);

        // Use passed search parameters or current state values
        const currentSearchKeyword = searchParams.searchKeyword !== undefined ? searchParams.searchKeyword : searchKeyword;
        const currentDealingOfficer = searchParams.dealingOfficer !== undefined ? searchParams.dealingOfficer : selectedDealingOfficer;
        const currentStage = searchParams.stage !== undefined ? searchParams.stage : selectedStage;

        try {
            const data = await JDApiService.getApplicationsList({
                appType,
                orgId,
                userTypeId,
                page: pageNum,
                limit: 50,
                searchKeyword: currentSearchKeyword,
                dealingOfficer: currentDealingOfficer !== 'all' ? currentDealingOfficer : null,
                stage: currentStage !== 'all' ? currentStage : null
            });

            if (data.success) {
                let applications = data.data.data.applications || [];

                // Sort by ApplicationNo in descending order for All Active CABs
                if (appType === 'allActiveCabs') {
                    applications = applications.sort((a, b) => {
                        const appNoA = a.ApplicationNo || a.applicationNo || '';
                        const appNoB = b.ApplicationNo || b.applicationNo || '';
                        return appNoB.localeCompare(appNoA, undefined, { numeric: true });
                    });
                }

                setApplications(applications);
                setApplicationsCount(data.data.data.stats || {});
                setFilteredApplications(applications);
                setTotalPages(data.data.data.totalPages || 1);
                setPage(pageNum);
                setSelectedAppType(appType);
            } else {
                throw new Error(data.message || 'Failed to load applications');
            }
        } catch (err) {
            setError(`Error loading applications: ${err.message}`);
            console.error('Load applications error:', err);
        } finally {
            setLoading(false);
        }
    }, [orgId, userTypeId]); // Only depend on stable values

    // Wrapper function to maintain compatibility
    const loadApplications = useCallback((appType, pageNum, searchParams) => {
        return loadApplicationsStable(appType, pageNum, searchParams);
    }, [loadApplicationsStable]);

    // Load dropdown options
    const loadDropdownOptions = useCallback(async () => {
        try {
            // Load joint directors/dealing officers using DO_Ammendments mode 6
            const jdData = await JDApiService.getJointDirectors({ orgId, userTypeId });

            if (jdData.success) {
                // Handle nested data structure - check if data.data exists or just data
                let jdArray = [];
                if (jdData.data && jdData.data.data) {
                    jdArray = Array.isArray(jdData.data.data) ? jdData.data.data : [];
                } else if (Array.isArray(jdData.data)) {
                    jdArray = jdData.data;
                } else {
                    jdArray = [];
                }
                setJointDirectors(jdArray);
            } else {
                console.error('Failed to load joint directors:', jdData.message);
                setJointDirectors([]);
            }

            // Load dealing officers for filter (if under process)
            if (selectedAppType === 'underProcess') {
                const doData = await JDApiService.getDealingOfficers({ orgId, userTypeId });
                if (doData.success) {
                    let doArray = [];
                    if (doData.data && doData.data.data) {
                        doArray = Array.isArray(doData.data.data) ? doData.data.data : [];
                    } else if (Array.isArray(doData.data)) {
                        doArray = doData.data;
                    } else {
                        doArray = [];
                    }
                    setDealingOfficers(doArray);
                } else {
                    setDealingOfficers([]);
                }
            }

            // Load stages (if JD user)
            if (userTypeId === '8') {
                const stagesData = await JDApiService.getStages();
                if (stagesData.success) {
                    const stagesArray = Array.isArray(stagesData.data) ? stagesData.data : [];
                    setStages(stagesArray);
                } else {
                    setStages([]);
                }
            }
        } catch (err) {
            console.error('Load dropdown options error:', err);
            setJointDirectors([]);
            setDealingOfficers([]);
            setStages([]);
        }
    }, [orgId, userTypeId, selectedAppType]);

    // View application
    const viewApplication = (app) => {
        try {
            const encryptedAppId = encryptAppId(app.AppId || app.appId);
            const encryptedOrgId = encryptText((app.OrgId || app.orgId)?.toString() || '');
            const encryptedLabTypeId = encryptText((app.LabTypeId || app.labTypeId)?.toString() || '');
            const queryParams = new URLSearchParams({
                appId: encryptedAppId,
                orgId: encryptedOrgId,
                labTypeId: encryptedLabTypeId
            });

            navigate(`/main/jd/applications/view?${queryParams.toString()}`);
        } catch (err) {
            console.error('Navigate to view application error:', err);
        }
    };


    // View disciplines
    const viewDisciplines = async (appId, labTypeId) => {
        try {
            const data = await JDApiService.getDisciplines({ appId, labTypeId });
            if (data.success) {
                setDisciplineData(data.data || []);
                setDisciplineDialogOpen(true);
            }
        } catch (err) {
            console.error('Load disciplines error:', err);
        }
    };

    // View groups
    const viewGroups = async (appId, labTypeId) => {
        try {
            const data = await JDApiService.getGroups({ appId, labTypeId });
            if (data.success) {
                setGroupData(data.data || []);
                setGroupDialogOpen(true);
            }
        } catch (err) {
            console.error('Load groups error:', err);
        }
    };

    const viewSCFDashboard = () => {
        try {
            const targetUrl = `https://nablwpnew.qci.org.in/SCF_Integration/Transfer.aspx?user=JD&orgid=${localStorage.getItem("orgId")}`;
            window.location.href = targetUrl;
        } catch (err) {
            console.error('Navigate to view application error:', err);
        }
    };

    const handleAdditionalAppDashboard = (appId) => {
        try {
            const targetUrl = `/main/jd/additional-app-dashboard`;
            window.location.href = targetUrl;
        } catch (err) {
            console.error('Navigate to view application error:', err);
        }
    };

    const handleILACAppDashboard = (appId) => {
        try {
            const targetUrl = `/main/jd/ilac-app-dashboard`;
            window.location.href = targetUrl;
        } catch (err) {
            console.error('Navigate to view application error:', err);
        }
    };

    // View assessors
    const viewAssessors = async (appId) => {
        setLoadingAssessors(true);
        try {
            const data = await JDApiService.getAssessors({ appId });

            if (data.success) {
                // Handle different response structures
                let assessors = [];

                if (data.data && data.data.data) {
                    // Nested structure: { success: true, data: { data: [...] } }
                    assessors = Array.isArray(data.data.data) ? data.data.data : [];
                } else if (data.data) {
                    // Direct structure: { success: true, data: [...] }
                    assessors = Array.isArray(data.data) ? data.data : [];
                } else {
                    assessors = [];
                }

                setAssessorsData(assessors);
                setAssessorsDialogOpen(true);
            } else {
                setError(data.message || 'Failed to load assessors');
            }
        } catch (err) {
            console.error('Load assessors error:', err);
            setError('Failed to load assessors');
        } finally {
            setLoadingAssessors(false);
        }
    };

    // Handle select all
    const handleSelectAll = (event) => {
        const checked = event.target.checked;
        setSelectAll(checked);

        if (checked) {
            setSelectedApplications(filteredApplications.map(app => app.AppId || app.appId));
        } else {
            setSelectedApplications([]);
        }
    };

    // Handle individual selection
    const handleSelectApplication = (appId) => {
        setSelectedApplications(prev => {
            if (prev.includes(appId)) {
                return prev.filter(id => id !== appId);
            } else {
                return [...prev, appId];
            }
        });
    };

    // Handle allotment
    const handleAllotment = async () => {
        // Validation
        if (selectedApplications.length === 0) {
            setError('No Application Selected.');
            return;
        }

        if (!selectedJointDirector) {
            setError('Please select a Joint Director/Dealing Officer');
            return;
        }

        if (!password || password.trim() === '') {
            setError('Please enter your password');
            return;
        }

        setLoading(true);
        setError(null);
        setSuccess(null);

        try {
            // Step 1: Verify password first
            const verifyData = await axios.post(
                `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/auth/verify-password`,
                {
                    orgId: orgId,
                    password: password,
                    appId: appId,
                }
            );


            if (!verifyData.data.success) {
                setError('Invalid Password');
                return;
            }


            const data = await JDApiService.allotApplications({
                applicationIds: selectedApplications,
                allotedToId: selectedJointDirector,
                password,
                orgId,
                userTypeId
            });


            if (data.success) {
                setSuccess(data.message || `Application(s) Alloted Successfully.`);

                // Close modal
                setAllotmentModalOpen(false);

                // Reset form
                setSelectedApplications([]);
                setSelectAll(false);
                setSelectedJointDirector('');
                setPassword('');

                // Reload applications list after short delay to show success message
                setTimeout(() => {
                    loadApplications(selectedAppType, page, {
                        searchKeyword: '',
                        dealingOfficer: 'all',
                        stage: 'all'
                    });
                }, 1500);
            } else {
                setError(data.message || 'Allotment failed. Please try again.');
            }
        } catch (err) {
            console.error('Allotment error:', err);
            setError('Invalid Password');
        } finally {
            setLoading(false);
        }
    };

    // Export to Excel - Convert JSON to Excel format
    const exportToExcel = async () => {
        try {
            setLoading(true);

            const response = await JDApiService.exportApplications({
                appType: selectedAppType,
                orgId,
                userTypeId,
                searchKeyword,
                dealingOfficer: selectedDealingOfficer !== 'all' ? selectedDealingOfficer : null,
                stage: selectedStage !== 'all' ? selectedStage : null
            });

            if (response.success && response.data) {
                // Handle the JSON data directly
                let applicationsData = response.data;

                if (!Array.isArray(applicationsData) || applicationsData.length === 0) {
                    setError('No application data found to export. Try adjusting your filters or check if there are applications in the current view.');
                    return;
                }

                // Create filename with simpler date format for compatibility
                const now = new Date();
                const dateStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
                const timeStr = `${String(now.getHours()).padStart(2, '0')}-${String(now.getMinutes()).padStart(2, '0')}`;
                const filename = `JD_Applications_${selectedAppType}_${dateStr}_${timeStr}.csv`;

                // Convert JSON to CSV format (Excel 2010 compatible)
                const headers = [
                    'Application No',
                    'Laboratory Name',
                    'Login Name',
                    'Address',
                    'City',
                    'State',
                    'Assessment Type',
                    'SCF Count',
                    'Lab Type',
                    'Size of Lab',
                    'Amount',
                    'Status',
                    'DO Name',
                    'Standard',
                    'Assessment Start Date',
                    'Assessment Stop Date',
                    'Disciplines',
                    'Submitted Date'
                ];

                // Create CSV content
                let csvContent = headers.join(',') + '\n';

                applicationsData.forEach(app => {
                    const row = [
                        `"${app.ApplicationNo || ''}"`,
                        `"${app.LaboratoryName || ''}"`,
                        `"${app.LoginName || ''}"`,
                        `"${app.Address || ''}"`,
                        `"${app.City || ''}"`,
                        `"${app.State || ''}"`,
                        `"${app.AssessmentType || ''}"`,
                        `"${app.scfCount || '0'}"`,
                        `"${app.LabType || ''}"`,
                        `"${app.LabSize || ''}"`,
                        `"${app.TotalAmount || ''}"`,
                        `"${app.Status || ''}"`,
                        `"${app.DOName || ''}"`,
                        `"${app.Standard || ''}"`,
                        `"${app.AssessmentStartDate || ''}"`,
                        `"${app.AssessmentStopDate || ''}"`,
                        `"${app.Disciplines || ''}"`,
                        `"${app.SubmitDate || ''}"`
                    ];
                    csvContent += row.join(',') + '\n';
                });

                // Create blob with CSV content
                const blob = new Blob([csvContent], {
                    type: 'text/csv;charset=utf-8;'
                });

                // Multiple download methods for maximum compatibility
                let downloadSuccess = false;

                // Method 1: Internet Explorer and Edge Legacy
                if (window.navigator && window.navigator.msSaveOrOpenBlob) {
                    try {
                        window.navigator.msSaveOrOpenBlob(blob, filename);
                        downloadSuccess = true;
                    } catch (e) {
                        console.warn('msSaveOrOpenBlob failed:', e);
                    }
                }

                // Method 2: Modern browsers
                if (!downloadSuccess) {
                    try {
                        const url = window.URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = url;
                        link.download = filename;
                        link.style.display = 'none';

                        // Add to DOM temporarily
                        document.body.appendChild(link);

                        // Trigger download
                        link.click();

                        // Clean up
                        setTimeout(() => {
                            try {
                                document.body.removeChild(link);
                                window.URL.revokeObjectURL(url);
                            } catch (cleanupError) {
                                console.warn('Cleanup error:', cleanupError);
                            }
                        }, 100);

                        downloadSuccess = true;
                    } catch (e) {
                        console.warn('Standard download failed:', e);
                    }
                }

                // Method 3: Fallback - open in new window
                if (!downloadSuccess) {
                    try {
                        const url = window.URL.createObjectURL(blob);
                        const newWindow = window.open(url, '_blank');
                        if (newWindow) {
                            setTimeout(() => {
                                window.URL.revokeObjectURL(url);
                            }, 1000);
                            downloadSuccess = true;
                        }
                    } catch (e) {
                        console.warn('Window open fallback failed:', e);
                    }
                }

                if (downloadSuccess) {
                    setSuccess(`CSV file downloaded successfully! ${applicationsData.length} records exported. You can open this file in Excel 2010.`);
                } else {
                    throw new Error('All download methods failed. Please try again or contact support.');
                }
            } else {
                throw new Error(response.message || 'No data received from server');
            }
        } catch (err) {
            console.error('Export error:', err);
            setError(`Failed to export data: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    // Handle Accept Assessor
    const handleAcceptAssessor = (assessor) => {
        setSelectedAssessor(assessor);
        setAssessorAcceptDialogOpen(true);
    };

    // Confirm Accept Assessor
    const confirmAcceptAssessor = async () => {
        if (!selectedAssessor) return;

        setLoadingAssessors(true);
        setError(null);

        try {
            // Extract assessorId properly - handle array format
            const assessorId = Array.isArray(selectedAssessor.Id) ? selectedAssessor.Id[0] : selectedAssessor.Id;
            const appId = Array.isArray(selectedAssessor.AppId) ? selectedAssessor.AppId[0] : selectedAssessor.AppId;

            const data = await JDApiService.acceptAssessor({
                appId,
                assessorId,
                orgId,
                userTypeId
            });

            if (data.success) {
                setSuccess('Assessor accepted successfully!');
                setAssessorAcceptDialogOpen(false);
                setSelectedAssessor(null);
                // Reload assessors list
                viewAssessors(appId);
                // Reload main applications list to reflect changes
                loadApplications(selectedAppType, page, {
                    searchKeyword,
                    dealingOfficer: selectedDealingOfficer,
                    stage: selectedStage
                });
            } else {
                setError(data.message || 'Failed to accept assessor');
            }
        } catch (err) {
            console.error('Accept assessor error:', err);
            setError(`Failed to accept assessor: ${err.message}`);
        } finally {
            setLoadingAssessors(false);
        }
    };

    // Handle Reject Assessor
    const handleRejectAssessor = (assessor) => {
        setSelectedAssessor(assessor);
        setAssessorRejectRemarks('');
        setAssessorRejectDialogOpen(true);
    };

    // Confirm Reject Assessor
    const confirmRejectAssessor = async () => {
        if (!selectedAssessor) return;

        if (!assessorRejectRemarks || assessorRejectRemarks.trim() === '') {
            setError('Please provide remarks for rejection');
            return;
        }

        setLoadingAssessors(true);
        setError(null);

        try {
            // Extract assessorId properly - handle array format
            const assessorId = Array.isArray(selectedAssessor.Id) ? selectedAssessor.Id[0] : selectedAssessor.Id;
            const appId = Array.isArray(selectedAssessor.AppId) ? selectedAssessor.AppId[0] : selectedAssessor.AppId;

            const data = await JDApiService.rejectAssessor({
                appId,
                assessorId,
                remarks: assessorRejectRemarks,
                orgId,
                userTypeId
            });

            if (data.success) {
                setSuccess('Assessor rejected successfully!');
                setAssessorRejectDialogOpen(false);
                setSelectedAssessor(null);
                setAssessorRejectRemarks('');
                // Reload assessors list
                viewAssessors(appId);
                // Reload main applications list to reflect changes
                loadApplications(selectedAppType, page, {
                    searchKeyword,
                    dealingOfficer: selectedDealingOfficer,
                    stage: selectedStage
                });
            } else {
                setError(data.message || 'Failed to reject assessor');
            }
        } catch (err) {
            console.error('Reject assessor error:', err);
            setError(`Failed to reject assessor: ${err.message}`);
        } finally {
            setLoadingAssessors(false);
        }
    };

    const isAcceptanceView =
        selectedAppType === 'assessmentAcceptance' ||
        selectedAppType === 'form11acceptance';

    // Handle search
    const handleSearch = () => {
        setPage(1);
        // Use current state values for search
        loadApplications(selectedAppType, 1, {
            searchKeyword,
            dealingOfficer: selectedDealingOfficer,
            stage: selectedStage
        });
    };

    // Handle button click
    const handleButtonClick = (appType) => {
        setPage(1);
        setSearchKeyword('');
        setSelectedDealingOfficer('all');
        setSelectedStage('all');
        setSelectAll(false);
        setSelectedApplications([]);

        // Store the selected tab in localStorage for persistence
        localStorage.setItem('jdSubmittedApplicationTab', appType);

        // Load applications with cleared filters
        loadApplications(appType, 1, {
            searchKeyword: '',
            dealingOfficer: 'all',
            stage: 'all'
        });
    };

    // Initial load - only run once on component mount
    useEffect(() => {
        loadApplicationsStable(selectedAppType, 1);
        loadDropdownOptions();
    }, [loadApplicationsStable, loadDropdownOptions]); // Remove searchKeyword and other changing dependencies

    // Update dropdown options when app type changes
    useEffect(() => {
        loadDropdownOptions();
    }, [selectedAppType, loadDropdownOptions]);

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom>
                Group Applications
            </Typography>

            <Divider sx={{ mb: 3 }} />

            {/* Action Buttons */}
            <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                    variant={selectedAppType === 'processCompleted' ? 'contained' : 'outlined'}
                    onClick={() => handleButtonClick('processCompleted')}
                    sx={{ height: 50 }}
                >
                    Process Completed ({applicationsCount?.processCompleted || 0})
                </Button>

                <Button
                    variant={selectedAppType === 'toBeProcessed' ? 'contained' : 'outlined'}
                    onClick={() => handleButtonClick('toBeProcessed')}
                    sx={{ height: 50 }}
                >
                  To be processed ({applicationsCount?.toBeProcessed || 0})
                </Button>

                {userTypeId === '9' && (
                    <Button
                        variant={selectedAppType === 'underProcess' ? 'contained' : 'outlined'}
                        onClick={() => handleButtonClick('underProcess')}
                        sx={{ height: 50 }}
                    >
                        Under Process ({applicationsCount?.toBeProcessed || 0})
                    </Button>
                )}

                <Button
                    variant={selectedAppType === 'assessmentAcceptance' ? 'contained' : 'outlined'}
                    onClick={() => handleButtonClick('assessmentAcceptance')}
                    sx={{ height: 50 }}
                >
                    Assessment Acceptance ({applicationsCount?.assessmentAcceptance || 0})
                </Button>
                <Button
                    variant={selectedAppType === 'form11acceptance' ? 'contained' : 'outlined'}
                    onClick={() => handleButtonClick('form11acceptance')}
                    sx={{ height: 50 }}
                >
                    Form 11 Approval ({applicationsCount?.form11acceptance || 0})
                </Button>

                <Button
                    variant={selectedAppType === 'allActiveCabs' ? 'contained' : 'outlined'}
                    onClick={() => handleButtonClick('allActiveCabs')}
                    sx={{ height: 50 }}
                >
                    All Active CABs ({applicationsCount?.allActiveCabs || 0})
                </Button>

                <Button
                    variant={selectedAppType === 'closedCabs' ? 'contained' : 'outlined'}
                    onClick={() => handleButtonClick('closedCabs')}
                    sx={{ height: 50 }}
                >
                    Closed CABs ({applicationsCount?.closedCabs || 0})
                </Button>
                <Button
                    variant={selectedAppType === 'pendingCertificateApproval' ? 'contained' : 'outlined'}
                    onClick={() => handleButtonClick('pendingCertificateApproval')}
                    sx={{ height: 50 }}
                >
                    Pending Certificate Approval ({applicationsCount?.pendingCertificateApproval || 0})
                </Button>
                <Button
                    variant={selectedAppType === 'approvedCertificateApproval' ? 'contained' : 'outlined'}
                    onClick={() => handleButtonClick('approvedCertificateApproval')}
                    sx={{ height: 50 }}
                >
                    Approved Certificate ({applicationsCount?.approvedCertificateApproval || 0})
                </Button>
                <Button
                    variant={selectedAppType === 'disapprovedCertificateApproval' ? 'contained' : 'outlined'}
                    onClick={() => handleButtonClick('disapprovedCertificateApproval')}
                    sx={{ height: 50 }}
                >
                    Disapproved Certificate ({applicationsCount?.disapprovedCertificateApproval || 0})
                </Button>
                <Button
                    variant='outlined'
                    onClick={viewSCFDashboard}
                    sx={{ height: 50, width: 100 }}
                >
                    SCF
                </Button>
                <Button
                    variant='outlined'
                    onClick={handleAdditionalAppDashboard}
                    sx={{ height: 50}}
                >
                    Other Activities during the accreditation cycle (Till Validity) Decision Approval
                </Button>
                <Button
                    variant='outlined'
                    onClick={handleILACAppDashboard}
                    sx={{ height: 50}}
                >
                    Permission to use NABL Accredited CAB combined ILAC MRA Mark
                </Button>
            </Box>

            {/* Filter Section */}
            <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <TextField
                    label="Keyword Filter"
                    placeholder="Search"
                    value={searchKeyword}
                    onChange={(e) => setSearchKeyword(e.target.value)}
                    size="small"
                    sx={{ minWidth: 200 }}
                />

                {selectedAppType === 'underProcess' && dealingOfficers.length > 0 && (
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel>Dealing Officer</InputLabel>
                        <Select
                            value={selectedDealingOfficer}
                            onChange={(e) => setSelectedDealingOfficer(e.target.value)}
                            label="Dealing Officer"
                        >
                            <MenuItem value="all">All Officers</MenuItem>
                            {Array.isArray(dealingOfficers) && dealingOfficers.map(officer => (
                                <MenuItem key={officer.id || officer.OrgId} value={officer.id || officer.OrgId}>
                                    {officer.name || officer.Name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}

                {userTypeId === '8' && stages.length > 0 && (
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel>Stage</InputLabel>
                        <Select
                            value={selectedStage}
                            onChange={(e) => setSelectedStage(e.target.value)}
                            label="Stage"
                        >
                            <MenuItem value="all">All Stages</MenuItem>
                            {Array.isArray(stages) && stages.map(stage => (
                                <MenuItem key={stage.stage || stage.Stage} value={stage.stage || stage.Stage}>
                                    {stage.description || stage.Description}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}

                <Button
                    variant="contained"
                    onClick={handleSearch}
                    disabled={loading}
                >
                    Search
                </Button>

                <Button
                    variant="contained"
                    onClick={exportToExcel}
                    disabled={loading}
                    startIcon={<GetAppIcon />}
                >
                    Export to Excel
                </Button>

                <Box sx={{ flexGrow: 1 }} />

                <Button
                    variant="outlined"
                    onClick={() => loadApplications(selectedAppType, page, {
                        searchKeyword,
                        dealingOfficer: selectedDealingOfficer,
                        stage: selectedStage
                    })}
                    startIcon={<RefreshIcon />}
                    disabled={loading}
                >
                    Refresh
                </Button>
            </Box>

            {/* Allot Selected Applications Button */}
            {selectedApplications.length > 0 && (
                <Box sx={{ mb: 2 }}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => setAllotmentModalOpen(true)}
                        startIcon={<SendIcon />}
                    >
                        Allot Selected Applications ({selectedApplications.length})
                    </Button>
                </Box>
            )}

            {/* Success Display */}
            {success && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
                    {success}
                </Alert>
            )}

            {/* Error Display */}
            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {/* Loading State */}
            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                    <CircularProgress />
                </Box>
            )}

            {/* Applications Table */}
            {!loading && applications.length > 0 && (
                <>
                    <TableContainer
                        component={Paper}
                        sx={{
                            boxShadow: 3,
                            borderRadius: 2,
                            overflow: 'auto',
                            mb: 3
                        }}
                    >
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    {!isAcceptanceView ? (
                                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                                            Sr. NO. / Actions
                                            <Checkbox
                                                checked={selectAll}
                                                onChange={handleSelectAll}
                                                sx={{ ml: 1 }}
                                            />
                                        </TableCell>
                                    ) : (
                                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                                            Sr. NO. / Actions
                                        </TableCell>
                                    )}
                                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', minWidth: 200 }}>
                                        Laboratory Details
                                    </TableCell>
                                    {selectedAppType !== 'assessmentAcceptance' && selectedAppType !== 'form11acceptance' && (
                                        <>
                                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                                                View Assessors
                                            </TableCell>
                                        </>
                                    )}
                                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                                        Concerned Team Assigned
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', minWidth: 250 }}>
                                        Location Details
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                                        Status
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                                        Submitted Date
                                    </TableCell>

                                    {selectedAppType === 'underProcess' && (
                                        <>
                                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                                                Assessment Start Date
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                                                Assessment Stop Date
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                                                Decision Date
                                            </TableCell>
                                        </>
                                    )}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {selectedAppType === 'assessmentAcceptance' || selectedAppType === 'form11acceptance' ? (
                                    // Assessment Acceptance View - Show only lab details with view button
                                    filteredApplications.map((app, index) => (
                                        <TableRow
                                            key={app.AppId || app.appId}
                                            sx={{
                                                '&:nth-of-type(odd)': { backgroundColor: '#fafafa' },
                                                '&:hover': { backgroundColor: '#f0f8ff' }
                                            }}
                                        >
                                            <TableCell>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                                                    <Typography variant="body2" fontWeight="bold">
                                                        {(page - 1) * pageSize + index + 1}
                                                    </Typography>
                                                    <Button
                                                        size="small"
                                                        variant="contained"
                                                        fullWidth
                                                        onClick={() => {
                                                            const encryptedAppId = encryptText((app.AppId || app.appId)?.toString() || '');
                                                            const encryptedOrgId = encryptText((app.OrgId || app.orgId)?.toString() || '');
                                                            const encryptedLabName = encryptText(app.LaboratoryName || app.laboratoryName || '');
                                                            const encryptedApplicationNo = encryptText(app.ApplicationNo || app.applicationNo || '');
                                                            const encryptedLabTypeId = encryptText((app.LabTypeId || app.labTypeId)?.toString() || '');

                                                            const queryParams = new URLSearchParams({
                                                                appId: encryptedAppId,
                                                                orgId: encryptedOrgId,
                                                                labTypeId: encryptedLabTypeId
                                                            });
                                                            navigate(`/main/jd/applications/view?${queryParams.toString()}`);
                                                        }}
                                                    >
                                                        View
                                                    </Button>

                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Box>
                                                    <Typography variant="body2" fontWeight="bold" color="primary">
                                                        {app.ApplicationNo || app.applicationNo}
                                                    </Typography>
                                                    <Typography variant="body2" fontWeight={600}>
                                                        {app.LaboratoryName || app.laboratoryName}
                                                    </Typography>
                                                    <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 1 }}>
                                                        UserName: {app.LoginName || app.loginName}
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                                        <Chip
                                                            label={`Standard: ${app.Standard || app.standard || 'N/A'}`}
                                                            size="small"
                                                            color="primary"
                                                            variant="outlined"
                                                        />
                                                        <Chip
                                                            label={`${app.AssessmentType || app.assessmentType}`}
                                                            size="small"
                                                            color="secondary"
                                                            variant="outlined"
                                                        />
                                                        <Chip
                                                            label={`${app.LabType || app.labType}`}
                                                            size="small"
                                                            color="info"
                                                            variant="outlined"
                                                        />
                                                    </Box>
                                                </Box>
                                            </TableCell>
                                            <TableCell>{app.DOName || app.doName || 'N/A'}</TableCell>
                                            <TableCell>
                                                <Box>
                                                    <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                                                        {app.Address || app.address}</Typography>
                                                    <Chip variant="filled" color='primary' label={`${app.City || app.city}, ${app.State || app.state}`} size="small" />
                                                </Box>
                                            </TableCell>

                                            <TableCell>
                                                <Chip
                                                    label={app.Status || app.status}
                                                    size="small"
                                                    color="warning"
                                                    sx={{
                                                        height: 'auto',
                                                        '& .MuiChip-label': {
                                                            whiteSpace: 'normal',
                                                            wordWrap: 'break-word'
                                                        }
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                {app.SubmitDate[0] || app.SubmitDate[0]}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    // Other Views - Standard table
                                    filteredApplications.map((app, index) => (
                                        <TableRow
                                            key={app.AppId || app.appId}
                                            sx={{
                                                '&:nth-of-type(odd)': { backgroundColor: '#fafafa' },
                                                '&:hover': { backgroundColor: '#f0f8ff' }
                                            }}
                                        >
                                            <TableCell>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                                                    <Typography variant="body2" fontWeight="bold">
                                                        {(page - 1) * pageSize + index + 1}
                                                    </Typography>
                                                    {selectedAppType !== 'assessmentAcceptance' && selectedAppType !== 'form11acceptance' && (
                                                        <Checkbox
                                                            checked={selectedApplications.includes(app.AppId || app.appId)}
                                                            onChange={() => handleSelectApplication(app.AppId || app.appId)}
                                                        />
                                                    )}
                                                    <Button
                                                        size="small"
                                                        variant="contained"
                                                        fullWidth
                                                        onClick={() => viewApplication(app)}
                                                    >
                                                        View
                                                    </Button>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Box>
                                                    <Typography variant="body2" fontWeight="bold" color="primary">
                                                        {app.ApplicationNo || app.applicationNo}
                                                    </Typography>
                                                    <Typography variant="body2" fontWeight={600}>
                                                        {app.LaboratoryName || app.laboratoryName}
                                                    </Typography>
                                                    <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 1 }}>
                                                        User: {app.LoginName || app.loginName}
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                                        <Chip
                                                            label={`Standard: ${app.Standard || app.standard || 'N/A'}`}
                                                            size="small"
                                                            color="primary"
                                                            variant="outlined"
                                                        />
                                                        <Chip
                                                            label={`Type: ${app.AssessmentType || app.assessmentType}`}
                                                            size="small"
                                                            color="secondary"
                                                            variant="outlined"
                                                        />
                                                        <Chip
                                                            label={`Field: ${app.LabType || app.labType}`}
                                                            size="small"
                                                            color="info"
                                                            variant="outlined"
                                                        />
                                                    </Box>
                                                </Box>
                                            </TableCell>
                                            {selectedAppType !== 'assessmentAcceptance' && selectedAppType !== 'form11acceptance' && (
                                                <>
                                                    <TableCell>
                                                        <Tooltip title="View Assessors">
                                                            <IconButton
                                                                size="small"
                                                                color="primary"
                                                                onClick={() => viewAssessors(app.AppId || app.appId)}
                                                                sx={{
                                                                    backgroundColor: '#e3f2fd',
                                                                    '&:hover': { backgroundColor: '#bbdefb' }
                                                                }}
                                                            >
                                                                <PersonIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </TableCell>
                                                </>
                                            )}
                                            <TableCell>{app.DOName || app.doName || 'N/A'}</TableCell>
                                            <TableCell>
                                                <Box>
                                                    <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                                                        {app.Address || app.address || 'N/A'}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {app.City || app.city}, {app.State || app.state}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={app.Status || app.status}
                                                    size="small"
                                                    color="warning"
                                                />
                                            </TableCell>
                                            {selectedAppType !== 'assessmentAcceptance' && selectedAppType !== 'form11acceptance' && (
                                                <>
                                                    <TableCell>{app.SubmitDate[0] || formatDate((app.SubmitDate[1]) || app.submitDate)}</TableCell>
                                                </>
                                            )}
                                            {selectedAppType === 'underProcess' && (
                                                <>
                                                    <TableCell>{formatDate(app.AssessmentStartDate || app.assessmentStartDate)}</TableCell>
                                                    <TableCell>{formatDate(app.AssessmentStopDate || app.assessmentStopDate)}</TableCell>
                                                    <TableCell>{formatDate(app.decisionDate)}</TableCell>
                                                </>
                                            )}
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* Pagination */}
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                        <Pagination
                            count={totalPages}
                            page={page}
                            onChange={(e, newPage) => loadApplications(selectedAppType, newPage, {
                                searchKeyword,
                                dealingOfficer: selectedDealingOfficer,
                                stage: selectedStage
                            })}
                            color="primary"
                            disabled={loading}
                        />
                    </Box>
                </>
            )}

            {/* No Data State */}
            {!loading && applications.length === 0 && !error && (
                <Paper sx={{ p: 8, textAlign: 'center', borderRadius: 2 }}>
                    <Typography variant="h6" color="textSecondary">
                        No applications found
                    </Typography>
                </Paper>
            )}

            {/* Allotment Modal */}
            <Dialog
                open={allotmentModalOpen}
                onClose={() => !loading && setAllotmentModalOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AssignmentIcon color="primary" />
                        Allotment of Application
                        {loading && <CircularProgress size={20} sx={{ ml: 1 }} />}
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Selected Applications: <strong>{selectedApplications.length}</strong>
                        </Typography>

                        <FormControl fullWidth required disabled={loading}>
                            <InputLabel>
                                {userTypeId === '8' ? 'Select Dealing Officer' : 'Select Dealing Officer'}
                            </InputLabel>
                            <Select
                                value={selectedJointDirector}
                                onChange={(e) => setSelectedJointDirector(e.target.value)}
                                label={userTypeId === '8' ? 'Select Dealing Officer' : 'Select Dealing Officer'}
                            >
                                <MenuItem value="">
                                    {userTypeId === '8' ? '--Select Dealing Officer--' : '--Select Dealing Officer--'}
                                </MenuItem>
                                {Array.isArray(jointDirectors) && jointDirectors.length > 0 ? (
                                    jointDirectors.map(jd => (
                                        <MenuItem
                                            key={jd.id || jd.Id || jd.OrgId}
                                            value={jd.id || jd.Id || jd.OrgId}
                                        >
                                            {jd.Name || jd.name}
                                            {(jd.Designation || jd.designation) &&
                                                ` - ${jd.Designation || jd.designation}`
                                            }
                                        </MenuItem>
                                    ))
                                ) : (
                                    <MenuItem disabled>
                                        No {userTypeId === '8' ? 'Dealing Officers' : 'Dealing Officer'} available
                                    </MenuItem>
                                )}
                            </Select>
                        </FormControl>

                        <TextField
                            label="Enter Your Password to Continue"
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            fullWidth
                            required
                            disabled={loading}
                            helperText="Enter your password to confirm the allotment"
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={() => {
                            setAllotmentModalOpen(false);
                            setSelectedJointDirector('');
                            setPassword('');
                            setError(null);
                        }}
                        variant="outlined"
                        color="error"
                        disabled={loading}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleAllotment}
                        variant="contained"
                        color="primary"
                        disabled={!selectedJointDirector || !password || loading}
                        startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
                    >
                        {loading ? 'Submitting...' : 'Submit Allotment'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Discipline Dialog */}
            <Dialog
                open={disciplineDialogOpen}
                onClose={() => setDisciplineDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Discipline</DialogTitle>
                <DialogContent>
                    <TableContainer sx={{ mt: 2 }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>S No.</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Discipline</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {disciplineData.map((item, index) => (
                                    <TableRow key={index}>
                                        <TableCell>{index + 1}</TableCell>
                                        <TableCell>{item.dis}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDisciplineDialogOpen(false)} variant="outlined">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Group Dialog */}
            <Dialog
                open={groupDialogOpen}
                onClose={() => setGroupDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Group</DialogTitle>
                <DialogContent>
                    <TableContainer sx={{ mt: 2 }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>S No.</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Discipline</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Group</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {groupData.map((item, index) => (
                                    <TableRow key={index}>
                                        <TableCell>{index + 1}</TableCell>
                                        <TableCell>{item.dis}</TableCell>
                                        <TableCell>{item.grp}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setGroupDialogOpen(false)} variant="outlined">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Assessors Dialog */}
            <Dialog
                open={assessorsDialogOpen}
                onClose={() => !loadingAssessors && setAssessorsDialogOpen(false)}
                maxWidth="lg"
                fullWidth
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonIcon />
                        Application Assessors
                        {loadingAssessors && <CircularProgress size={20} />}
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {assessorsData.length === 0 && !loadingAssessors ? (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <Typography variant="h6" color="textSecondary">
                                No assessors found for this application
                            </Typography>
                        </Box>
                    ) : (
                        <TableContainer sx={{ mt: 2 }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>S No.</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Action</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Assessor Name</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Email</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Phone</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Designation</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Experience</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Status</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Proposed Date</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {assessorsData.map((assessor, index) => {
                                        // Handle array fields properly
                                        const jDirectorAccepted = Array.isArray(assessor.JDirectorAccepted)
                                            ? assessor.JDirectorAccepted[0]
                                            : assessor.JDirectorAccepted;

                                        const assessorId = Array.isArray(assessor.Id) ? assessor.Id[0] : assessor.Id;

                                        return (
                                            <TableRow
                                                key={assessorId || index}
                                                sx={{
                                                    '&:nth-of-type(odd)': { backgroundColor: '#fafafa' },
                                                    '&:hover': { backgroundColor: '#f0f8ff' }
                                                }}
                                            >
                                                <TableCell>{index + 1}</TableCell>
                                                <TableCell>

                                                    <Chip
                                                        label={jDirectorAccepted === 1 ? 'Accepted' : 'Rejected'}
                                                        size="small"
                                                        color={jDirectorAccepted === 1 ? 'success' : 'error'}
                                                    />
                                                </TableCell>
                                                <TableCell>{assessor.AssessorName || 'N/A'}</TableCell>
                                                <TableCell>{assessor.Email || 'N/A'}</TableCell>
                                                <TableCell>{assessor.Mobile || 'N/A'}</TableCell>
                                                <TableCell>{assessor.Capacity || 'N/A'}</TableCell>
                                                <TableCell>N/A</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={
                                                            jDirectorAccepted === 1 ? 'Accepted' :
                                                                jDirectorAccepted === 2 ? 'Rejected' :
                                                                    'Pending'
                                                        }
                                                        size="small"
                                                        color={
                                                            jDirectorAccepted === 1 ? 'success' :
                                                                jDirectorAccepted === 2 ? 'error' :
                                                                    'warning'
                                                        }
                                                    />
                                                </TableCell>
                                                <TableCell>{formatDate(assessor.DateFrom)}</TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setAssessorsDialogOpen(false)}
                        variant="outlined"
                        disabled={loadingAssessors}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Assessor Accept Confirmation Dialog */}
            <Dialog
                open={assessorAcceptDialogOpen}
                onClose={() => !loadingAssessors && setAssessorAcceptDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Confirm Accept Assessor</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Are you sure you want to accept this assessor?
                    </Typography>
                    {selectedAssessor && (
                        <Box sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 1 }}>
                            <Typography variant="body2">
                                Assessor Name: {selectedAssessor.AssessorName || 'N/A'}
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 1 }}>
                                Email: {selectedAssessor.Email || 'N/A'}
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 1 }}>
                                Capacity: {selectedAssessor.Capacity || 'N/A'}
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setAssessorAcceptDialogOpen(false)}
                        disabled={loadingAssessors}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={confirmAcceptAssessor}
                        variant="contained"
                        color="success"
                        disabled={loadingAssessors}
                        startIcon={loadingAssessors ? <CircularProgress size={20} /> : <CheckIcon />}
                    >
                        {loadingAssessors ? 'Accepting...' : 'Accept'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Assessor Reject Confirmation Dialog */}
            <Dialog
                open={assessorRejectDialogOpen}
                onClose={() => !loadingAssessors && setAssessorRejectDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Reject Assessor</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Please provide remarks for rejecting this assessor:
                    </Typography>
                    {selectedAssessor && (
                        <Box sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 1, mb: 2 }}>
                            <Typography variant="body2">
                                Assessor Name: {selectedAssessor.AssessorName || 'N/A'}
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 1 }}>
                                Email: {selectedAssessor.Email || 'N/A'}
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 1 }}>
                                Capacity: {selectedAssessor.Capacity || 'N/A'}
                            </Typography>
                        </Box>
                    )}
                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label="Remarks *"
                        placeholder="Enter reason for rejection..."
                        value={assessorRejectRemarks}
                        onChange={(e) => setAssessorRejectRemarks(e.target.value)}
                        disabled={loadingAssessors}
                        error={!assessorRejectRemarks && error}
                        helperText={!assessorRejectRemarks && error ? 'Remarks are required' : ''}
                        sx={{ mt: 2 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setAssessorRejectDialogOpen(false);
                            setAssessorRejectRemarks('');
                        }}
                        disabled={loadingAssessors}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={confirmRejectAssessor}
                        variant="contained"
                        color="error"
                        disabled={loadingAssessors}
                        startIcon={loadingAssessors ? <CircularProgress size={20} /> : <CloseIcon />}
                    >
                        {loadingAssessors ? 'Rejecting...' : 'Reject'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default JDSubmittedApplication;
